"use client"

import { useEffect, useRef, useState, useCallback } from "react"
import { X, Play, Square, PlugsConnected, Plugs, ChevronDown, ChevronUp } from "@/lib/icons"
import { deviceController } from "@/lib/device-controller"
import { useDeviceStore } from "@/lib/device-store"
import { cn } from "@/lib/utils"
import { useTranslations } from "next-intl"

interface DeviceXYPadProps {
  onClose: () => void
  className?: string
}

const MIN_FREQ = 0.1
const MAX_FREQ = 3.0

export function DeviceXYPad({ onClose, className }: DeviceXYPadProps) {
  const t = useTranslations("deviceXyPad")
  const { getActiveDevice } = useDeviceStore()
  const activeDevice = getActiveDevice()
  const isConnected = !!activeDevice?.isConnected

  const padRef = useRef<HTMLDivElement>(null)
  const animRef = useRef<number | null>(null)
  const startTimeRef = useRef<number>(performance.now())
  const positionRef = useRef({ x: 0, y: 0 })       // smoothed — used for device commands
  const targetPosRef = useRef({ x: 0, y: 0 })       // raw pointer target — used for dot visuals
  const holdingRef = useRef(false)
  const isPlayingRef = useRef(false)
  const lastCmdTimeRef = useRef<number>(0)
  const isConnectedRef = useRef(isConnected)
  isConnectedRef.current = isConnected

  const [isPlaying, setIsPlaying] = useState(false)
  const [minimized, setMinimized] = useState(false)
  const [holding, setHolding] = useState(false)
  const [readout, setReadout] = useState({ freq: MIN_FREQ, amp: 0 })

  const stopLoop = useCallback(() => {
    if (animRef.current !== null) {
      cancelAnimationFrame(animRef.current)
      animRef.current = null
    }
  }, [])

  const runLoop = useCallback(() => {
    stopLoop()
    startTimeRef.current = performance.now()
    lastCmdTimeRef.current = 0
    const tick = () => {
      if (!isPlayingRef.current) return
      const now = performance.now()

      // Lerp smoothed position toward pointer target every frame (~60fps)
      // lerpFactor 0.15 → reaches ~91% of target in ~250ms — smooth without feeling sluggish
      const LERP = 0.15
      const cur = positionRef.current
      const tgt = targetPosRef.current
      positionRef.current = {
        x: cur.x + (tgt.x - cur.x) * LERP,
        y: cur.y + (tgt.y - cur.y) * LERP,
      }

      // Throttle device commands to 20fps (50ms) — rAF runs at 60fps
      if (now - lastCmdTimeRef.current >= 50) {
        const { x, y } = positionRef.current
        const freq = MIN_FREQ + x * (MAX_FREQ - MIN_FREQ)
        const amplitude = y * 100
        const elapsed = (now - startTimeRef.current) / 1000
        const raw = Math.sin(2 * Math.PI * freq * elapsed)
        const pos = Math.max(0, Math.min(100, Math.round(50 + (amplitude / 2) * raw)))
        if (isConnected) deviceController.setPosition(pos, undefined, true).catch(() => {})
        lastCmdTimeRef.current = now
      }
      animRef.current = requestAnimationFrame(tick)
    }
    animRef.current = requestAnimationFrame(tick)
  }, [isConnected, stopLoop])

  const handleTogglePlay = useCallback(() => {
    setIsPlaying((prev) => {
      const next = !prev
      isPlayingRef.current = next
      if (next) runLoop()
      else {
        stopLoop()
        if (isConnected) deviceController.setPosition(50).catch(() => {})
      }
      return next
    })
  }, [runLoop, stopLoop, isConnected])

  // Unmount-only cleanup. This previously listed `isConnected` as a dependency,
  // so React ran this cleanup on every connection-state change, not just
  // unmount — it canceled the animation loop but never reset `isPlaying`, so
  // the "ON" label and pulse dot kept showing after the loop was already dead
  // (e.g. reconnecting mid-play). Empty deps + a ref for the one thing the
  // cleanup still needs (whether to re-center the device) makes this fire only
  // on real unmount.
  useEffect(() => () => {
    stopLoop()
    if (isConnectedRef.current) deviceController.setPosition(50).catch(() => {})
  }, [stopLoop])

  // A live disconnect while playing left the loop's closure holding a stale
  // `isConnected=true` (captured when runLoop/handleTogglePlay last ran), so it
  // kept calling setPosition against a device that was no longer there, and the
  // UI still showed "ON". Stop cleanly and flip the state the moment the device
  // actually drops.
  useEffect(() => {
    if (!isConnected && isPlayingRef.current) {
      isPlayingRef.current = false
      stopLoop()
      setIsPlaying(false)
    }
  }, [isConnected, stopLoop])

  // Seed initial dot position CSS vars on mount and sync refs
  useEffect(() => {
    const el = padRef.current
    if (!el) return
    targetPosRef.current = { ...positionRef.current }
    el.style.setProperty("--dot-x", `${positionRef.current.x * 100}%`)
    el.style.setProperty("--dot-y", `${(1 - positionRef.current.y) * 100}%`)
  }, [])

  // Write CSS vars directly onto the pad element — no React render cycle, zero lag
  // targetPosRef is the raw pointer position; positionRef is the lerped value used by the device
  const updateFromEvent = useCallback((clientX: number, clientY: number) => {
    const el = padRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const nx = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
    const ny = Math.max(0, Math.min(1, 1 - (clientY - rect.top) / rect.height))
    targetPosRef.current = { x: nx, y: ny }
    // Dot visuals follow the finger instantly via CSS vars — no state, no re-render
    el.style.setProperty("--dot-x", `${nx * 100}%`)
    el.style.setProperty("--dot-y", `${(1 - ny) * 100}%`)
    // Readout reflects the target immediately so the display feels responsive
    setReadout({ freq: MIN_FREQ + nx * (MAX_FREQ - MIN_FREQ), amp: Math.round(ny * 100) })
  }, [])

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.currentTarget.setPointerCapture(e.pointerId)
    holdingRef.current = true
    setHolding(true)
    updateFromEvent(e.clientX, e.clientY)
  }, [updateFromEvent])

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!holdingRef.current) return
    e.preventDefault()
    updateFromEvent(e.clientX, e.clientY)
  }, [updateFromEvent])

  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.releasePointerCapture(e.pointerId)
    holdingRef.current = false
    setHolding(false)
  }, [])

  // ── Minimized pill — floats inline with zoom controls (bottom-right) ──
  if (minimized) {
    return (
      <div
        className={cn(
          "absolute bottom-[18px] right-3 z-30 pointer-events-auto select-none",
          "flex items-center gap-1 px-1.5 h-9 rounded-full",
          "bg-blue-950/80 backdrop-blur-sm border border-blue-500/30 shadow-lg",
          className,
        )}
      >
        {/* Play / Stop */}
        <button
          type="button"
          onClick={handleTogglePlay}
          disabled={!isConnected}
          className={cn(
            "w-7 h-7 rounded-full flex items-center justify-center transition-all",
            isPlaying
              ? "bg-red-500/80 hover:bg-red-500 text-white"
              : "bg-blue-500/70 hover:bg-blue-500 text-white",
            !isConnected && "opacity-30 cursor-not-allowed",
          )}
          title={isPlaying ? t("stopDevice") : t("startDevice")}
        >
          {isPlaying
            ? <Square className="w-3 h-3 fill-white" />
            : <Play className="w-3 h-3 fill-white ml-0.5" />
          }
        </button>

        {/* Running pulse */}
        {isPlaying && (
          <span className="relative flex h-1.5 w-1.5 shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-blue-400" />
          </span>
        )}

        {/* Expand */}
        <button
          type="button"
          className="w-7 h-7 rounded-full flex items-center justify-center text-blue-300/70 hover:text-blue-200 hover:bg-blue-500/20 transition-colors"
          onClick={() => setMinimized(false)}
          title={t("expandDeviceControl")}
        >
          <ChevronUp className="w-3.5 h-3.5" />
        </button>

        {/* Close */}
        <button
          type="button"
          className="w-7 h-7 rounded-full flex items-center justify-center text-blue-300/50 hover:text-white hover:bg-blue-500/20 transition-colors"
          onClick={(e) => { e.stopPropagation(); onClose() }}
          title={t("closeDeviceControl")}
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    )
  }

  // ── Full pad ────────────────────────────────────────────────────
  return (
    <div
      className={cn(
        "absolute bottom-0 left-0 right-0 z-30 pointer-events-auto select-none h-[200px]",
        "bg-black/85 backdrop-blur-sm border-t border-white/10",
        className,
      )}
    >
      {/* Top drag handle / header */}
      <div className="flex items-center justify-between px-3 pt-1.5 pb-0">
        <div className="flex items-center gap-1.5">
          {isConnected
            ? <PlugsConnected className="w-3 h-3 text-green-400" />
            : <Plugs className="w-3 h-3 text-red-400" />
          }
          <span className={cn("text-[10px] font-medium", isConnected ? "text-green-400" : "text-red-400/70")}>
            {isConnected ? activeDevice?.name?.slice(0, 12) || "Device" : "No device connected"}
          </span>
          {isPlaying && (
            <span className="relative flex h-1.5 w-1.5 ml-0.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-primary" />
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="w-5 h-5 rounded flex items-center justify-center text-white/40 hover:text-white/80 hover:bg-white/10 transition-colors"
            onClick={() => setMinimized(true)}
            title={t("minimize")}
          >
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            className="w-5 h-5 rounded flex items-center justify-center text-white/40 hover:text-white/80 hover:bg-white/10 transition-colors"
            onClick={(e) => { e.stopPropagation(); onClose() }}
            title={t("close")}
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Main content row */}
      <div className="flex h-[164px] gap-3 px-3 pb-3 pt-1">

        {/* Play/Stop button */}
        <div className="flex flex-col items-center justify-center gap-1.5 shrink-0">
          <button
            type="button"
            onClick={handleTogglePlay}
            disabled={!isConnected}
            className={cn(
              "w-11 h-11 rounded-full flex items-center justify-center transition-all shadow-lg",
              isPlaying
                ? "bg-red-500/90 hover:bg-red-600 shadow-red-500/30"
                : "bg-primary/90 hover:bg-primary shadow-primary/30",
              !isConnected && "opacity-25 cursor-not-allowed",
            )}
            title={isPlaying ? t("stop") : t("start")}
          >
            {isPlaying
              ? <Square className="w-4 h-4 fill-white text-white" />
              : <Play className="w-4 h-4 fill-white text-white ml-0.5" />
            }
          </button>
          <span className={cn(
            "text-[9px] font-mono tracking-wide",
            isPlaying ? "text-primary" : "text-white/30",
          )}>
            {isPlaying ? "ON" : "OFF"}
          </span>
        </div>

        {/* XY pad */}
        <div className="flex-1 flex flex-col gap-0.5 min-w-0">
          <div className="flex justify-between text-[9px] text-white/30 px-0.5">
            <span>{t("slow")}</span>
            <span className="text-white/50 font-medium">← SPEED →</span>
            <span>{t("fast")}</span>
          </div>

          <div className="relative flex-1">
            <div className="absolute -left-3.5 top-1/2 -translate-y-1/2 -rotate-90 text-[9px] text-white/30 pointer-events-none whitespace-nowrap">
              ↑ DEPTH
            </div>

            <div
              ref={padRef}
              className={cn(
                "w-full h-full rounded-md border cursor-crosshair touch-none relative overflow-hidden transition-colors",
                holding
                  ? "border-primary/60 bg-white/10"
                  : isPlaying
                  ? "border-white/20 bg-white/[0.06]"
                  : "border-white/10 bg-white/[0.03]",
              )}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
            >
              <svg className="absolute inset-0 w-full h-full" aria-hidden>
                {[25, 50, 75].map((p) => (
                  <line key={`v${p}`} x1={`${p}%`} y1="0" x2={`${p}%`} y2="100%"
                    stroke="white" strokeWidth="0.5" strokeOpacity="0.1" strokeDasharray="3 3" />
                ))}
                <line x1="0" y1="50%" x2="100%" y2="50%"
                  stroke="white" strokeWidth="0.5" strokeOpacity="0.1" strokeDasharray="3 3" />
              </svg>

              {/* Dot */}
              <div
                className={cn(
                  "absolute w-7 h-7 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 transition-all [left:var(--dot-x)] [top:var(--dot-y)]",
                  isPlaying ? "border-primary bg-primary/50 shadow-[0_0_8px_3px_hsl(var(--primary)/0.4)]" : "border-white/70 bg-white/20",
                  holding && "scale-110",
                )}
              />
              <div className="absolute top-0 bottom-0 w-px bg-white/8 pointer-events-none [left:var(--dot-x)]" />
              <div className="absolute left-0 right-0 h-px bg-white/8 pointer-events-none [top:var(--dot-y)]" />
            </div>
          </div>

          {/* Readout */}
          <div className="flex justify-center gap-3 text-[9px] text-white/40">
            <span>
              {t("speed")} <span className={cn("font-mono", isPlaying ? "text-primary/90" : "text-white/60")}>
                {t("freqHz", { freq: readout.freq.toFixed(1) })}
              </span>
            </span>
            <span className="text-white/20">·</span>
            <span>
              {t("depth")} <span className={cn("font-mono", isPlaying ? "text-primary/90" : "text-white/60")}>
                {readout.amp}%
              </span>
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
